import { useQuery } from '@apollo/client';
import {
  A3_BADGE_PATH,
  A3BadgeNames,
  A3UserStatistics,
  GET_A3_STATISTICS_FOR_USER,
  GET_ME,
  Me,
  User,
} from '@frontend/data-access';
import { Col, Divider, Empty, Row } from 'antd';
import getConfig from 'next/config';
import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';

import CompanyAvatar from '../../../company-avatar/company-avatar';

function UserProfileAbout({ data }: { data: User }) {
  const { publicRuntimeConfig } = getConfig();
  const { t } = useTranslation('common');
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const isInternalUser = useMemo(
    () => ['ADMIN', 'INTRAPRENEUR'].includes(meData?.me?.role as string),
    [meData]
  );

  const { data: a3StatisticsData } = useQuery<{
    getA3StatisticsForUser: A3UserStatistics;
  }>(GET_A3_STATISTICS_FOR_USER, {
    skip: !data?.id,
    variables: {
      userId: data?.id,
    },
  });

  const companies =
    data?.companies?.filter(
      (company) => company.approvalStatus === 'approved'
    ) || [];

  const a3Data: Record<string, string> = {
    [A3BadgeNames.Novice]: `${publicRuntimeConfig.staticFolder}${A3_BADGE_PATH?.NOVICE}`,
    [A3BadgeNames.Apprentice]: `${publicRuntimeConfig.staticFolder}${A3_BADGE_PATH?.APPRENTICE}`,
    [A3BadgeNames.Pro]: `${publicRuntimeConfig.staticFolder}${A3_BADGE_PATH?.PRO}`,
    [A3BadgeNames.Master]: `${publicRuntimeConfig.staticFolder}${A3_BADGE_PATH?.MASTER}`,
  };

  const a3Badge = data?.badge?.length
    ? a3Data[data.badge[data.badge.length - 1].name]
    : null;

  const totalStatistics = useMemo(() => {
    if (!a3StatisticsData?.getA3StatisticsForUser) {
      return 0;
    }
    return (
      (a3StatisticsData?.getA3StatisticsForUser.owned || 0) +
      (a3StatisticsData?.getA3StatisticsForUser.sponsored || 0) +
      (a3StatisticsData?.getA3StatisticsForUser.coached || 0) +
      (a3StatisticsData?.getA3StatisticsForUser.member || 0)
    );
  }, [a3StatisticsData]);

  return (
    <div className="space-y-6">
      {isInternalUser && (a3Badge || totalStatistics > 0) && (
        <div className="space-y-4 mb-4">
          <div className="text-gray-900 text-sm font-semibold">
            {t('a3_badge')}
          </div>
          <div className="flex items-center gap-4">
            {a3Badge && (
              <Image src={a3Badge} alt="a3 badge" width={64} height={64} />
            )}
            <div className="flex flex-col gap-1">
              <div className="text-gray-700">{t('a3_number_of_a3s')}</div>
              <div className="text-2xl font-medium text-gray-900">
                {totalStatistics}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 divide-x-2 divide-gray-200">
            <div className="flex flex-col gap-1">
              <div className="text-gray-700">{t('a3_owned')}</div>
              <div className="text-2xl font-medium text-gray-900">
                {a3StatisticsData?.getA3StatisticsForUser?.owned || 0}
              </div>
            </div>

            <div className="flex flex-col gap-1 pl-3">
              <div className="text-gray-700">{t('a3_sponsored')}</div>
              <div className="text-2xl font-medium text-gray-900">
                {a3StatisticsData?.getA3StatisticsForUser?.sponsored || 0}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 divide-x-2 divide-gray-200">
            <div className="flex flex-col gap-1">
              <div className="text-gray-700">{t('a3_coached')}</div>
              <div className="text-2xl font-medium text-gray-900">
                {a3StatisticsData?.getA3StatisticsForUser?.coached || 0}
              </div>
            </div>

            <div className="flex flex-col gap-1 pl-3">
              <div className="text-gray-700">{t('a3_teammember')}</div>
              <div className="text-2xl font-medium text-gray-900">
                {a3StatisticsData?.getA3StatisticsForUser?.member || 0}
              </div>
            </div>
          </div>
          <Divider />
        </div>
      )}
      <div className="space-y-3">
        <div className="text-gray-900 text-sm font-semibold">
          {t('biography')}
        </div>
        {data?.profile?.shortBio ? (
          <div>{data?.profile?.shortBio}</div>
        ) : (
          <div className="text-gray-700">{t('no_information')}</div>
        )}
      </div>
      <div className="space-y-3">
        <div className="text-gray-900 text-sm font-semibold">{t('skills')}</div>
        {data?.skills?.length ? (
          <Row className="gap-2 mt-3">
            {data.skills.map((skill, index) => {
              return (
                <Col
                  className="bg-gray-200 py-1 px-3 rounded-full text-xs"
                  key={index}
                >
                  {skill.name}
                </Col>
              );
            })}
          </Row>
        ) : (
          <div className="text-gray-700">{t('no_information')}</div>
        )}
      </div>
      <div className="space-y-3">
        <div className="text-gray-900 text-sm font-semibold">
          {t('interested_topics')}
        </div>
        <Row className="gap-2 mt-3">
          {data?.topics?.length ? (
            data.topics.map((topic, index) => {
              return (
                <Col
                  className="bg-gray-200 py-1 px-3 rounded-full text-xs"
                  key={index}
                >
                  {topic.name}
                </Col>
              );
            })
          ) : (
            <div className="text-gray-700">{t('no_information')}</div>
          )}
        </Row>
      </div>
      <div className="space-y-3">
        <div className="text-gray-900 text-sm font-semibold">
          {t('interested_region')}
        </div>
        <Row className="gap-2 mt-3">
          {data?.regions?.length ? (
            data.regions.map((region, index) => {
              return (
                <Col
                  className="bg-gray-200 py-1 px-3 rounded-full text-xs"
                  key={index}
                >
                  {region.name}
                </Col>
              );
            })
          ) : (
            <div className="text-gray-700">{t('no_information')}</div>
          )}
        </Row>
      </div>
      <div className="space-y-3">
        <div className="text-gray-900 text-sm font-semibold">
          {t('company')}
        </div>
        {companies.length ? (
          <div className="grid md:grid-cols-2 gap-4">
            {companies.map((company, index) => {
              return (
                <div key={index} className="bg-gray-200 p-4 rounded-xl">
                  <div className="flex gap-3">
                    <CompanyAvatar
                      size={64}
                      company={company}
                      showBadge={company.companyPortfolio?.isScvPortfolio}
                    />
                    <div className="w-full">
                      <div>
                        <Link
                          href={'/company/' + company.id}
                          className="text-base text-gray-900"
                        >
                          {company.name}
                        </Link>
                      </div>
                      <span className="line-clamp-2">
                        {company.description}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )}
      </div>
    </div>
  );
}

export default UserProfileAbout;
